import { createSlice } from '@reduxjs/toolkit'
import { fetchMacroProcesses, fetchTemplatesForPurpose, createMacroProcess } from './services/macroProcessService'
import { addContainersToMacroProcess } from '../macro-process-edit/services/macroProcessEditService'

const PURPOSE_LABELS = {
  vente: 'Process de vente',
  location: 'Process de location',
}

const initialState = {
  modalOpen: false,
  activePurpose: null,
  name: '',
  existingProcesses: [],
  availableTemplates: [],
  selectedTemplates: [], // [{ templateId, position }]
  loadingStatus: {
    fetchTemplates: 'idle',
    create: 'idle',
  },
  requestStatus: {
    success: false,
    error: null,
    errors: {},
  },
  error: null,
}

const macroProcessCreationSlice = createSlice({
  name: 'macroProcessCreation',
  initialState,
  reducers: {
    openModal: (state, action) => {
      state.modalOpen = true
      state.activePurpose = action.payload
      state.name = PURPOSE_LABELS[action.payload] || ''
      state.availableTemplates = []
      state.selectedTemplates = []
      state.error = null
      state.requestStatus = initialState.requestStatus
    },
    closeModal: (state) => {
      state.modalOpen = false
      state.activePurpose = null
      state.name = ''
      state.availableTemplates = []
      state.selectedTemplates = []
      state.error = null
      state.requestStatus = initialState.requestStatus
    },
    setName: (state, action) => {
      state.name = action.payload
    },
    toggleTemplate: (state, action) => {
      const templateId = action.payload
      const index = state.selectedTemplates.findIndex(t => t.templateId === templateId)
      if (index !== -1) {
        state.selectedTemplates.splice(index, 1)
      } else {
        const nextPosition = state.selectedTemplates.length + 1
        state.selectedTemplates.push({ templateId, position: nextPosition })
      }
    },
    setTemplatePosition: (state, action) => {
      const { templateId, position } = action.payload
      const item = state.selectedTemplates.find(t => t.templateId === templateId)
      if (item) {
        item.position = position
      }
    },
    resetRequestStatus: (state) => {
      state.requestStatus = initialState.requestStatus
    },
  },
  extraReducers: (builder) => {
    // fetchMacroProcesses
    builder.addCase(fetchMacroProcesses.pending, (state) => {
      state.loadingStatus.fetchMacroProcesses = 'pending'
      state.error = null
    })
    builder.addCase(fetchMacroProcesses.fulfilled, (state, action) => {
      state.loadingStatus.fetchMacroProcesses = 'fulfilled'
      state.existingProcesses = action.payload.macro_processes
    })
    builder.addCase(fetchMacroProcesses.rejected, (state, action) => {
      state.loadingStatus.fetchMacroProcesses = 'rejected'
      state.error = action.payload
    })

    // fetchTemplatesForPurpose
    builder.addCase(fetchTemplatesForPurpose.pending, (state) => {
      state.loadingStatus.fetchTemplates = 'pending'
      state.error = null
    })
    builder.addCase(fetchTemplatesForPurpose.fulfilled, (state, action) => {
      state.loadingStatus.fetchTemplates = 'fulfilled'
      state.availableTemplates = action.payload.templates || []
    })
    builder.addCase(fetchTemplatesForPurpose.rejected, (state, action) => {
      state.loadingStatus.fetchTemplates = 'rejected'
      state.error = action.payload
    })

    // addContainersToMacroProcess — synchronise existingProcesses avec les données fraîches
    builder.addCase(addContainersToMacroProcess.fulfilled, (state, action) => {
      const updated = action.payload
      const index = state.existingProcesses.findIndex(p => p.id === updated.id)
      if (index !== -1) {
        state.existingProcesses[index] = updated
      }
    })

    // createMacroProcess
    builder.addCase(createMacroProcess.pending, (state) => {
      state.loadingStatus.create = 'pending'
      state.error = null
      state.requestStatus.error = null
      state.requestStatus.errors = {}
      state.requestStatus.success = false
    })
    builder.addCase(createMacroProcess.fulfilled, (state) => {
      state.loadingStatus.create = 'fulfilled'
      state.modalOpen = false
      state.activePurpose = null
      state.name = ''
      state.selectedTemplates = []
      state.availableTemplates = []
      state.requestStatus.success = true
      state.requestStatus.errors = {}
      state.requestStatus.error = null
    })
    builder.addCase(createMacroProcess.rejected, (state, action) => {
      state.loadingStatus.create = 'rejected'
      state.error = action.payload
      const errors = action.payload?.errors ? action.payload.errors[0] : {}
      if(errors.position) {
        errors.position = "Les sous-processes sélectionnés doivent avoir un ordre unique"
      }
      state.requestStatus.errors =  errors
      state.requestStatus.error = action.payload
      state.requestStatus.success = false
    })
  },
})

export const {
  openModal,
  closeModal,
  setName,
  toggleTemplate,
  setTemplatePosition,
  resetRequestStatus,
} = macroProcessCreationSlice.actions

export const selectPendingVenteCount = (state) =>
  state.macroProcessCreation.existingProcesses.filter(
    (p) => p.purpose === 'vente' && p.status === 'pending'
  ).length

export const selectPendingLocationCount = (state) =>
  state.macroProcessCreation.existingProcesses.filter(
    (p) => p.purpose === 'location' && p.status === 'pending'
  ).length

export default macroProcessCreationSlice.reducer
